import { EnvironmentProviders, Injectable, inject } from '@angular/core';
import { buildStepToWorkflowMap, provideWorkflowTracking, TrackWorkflowService } from '@aiwyn/shared';
import {
  PaymentEventEventTypeMap,
  PaymentWorkflowEvent,
  PaymentWorkflowEventMetaDataMap,
} from './payment-workflow.models';
import { PaymentsWorkflowType } from '../payments-workflow.models';

/**
 * Tracker for Flow 6 (Making a Payment). Narrows `TrackWorkflowService` to the payment step enum
 * + metadata map and exposes `startOperation` / `endOperation` for stamping `operationDurationMs`
 * on CHARGE_SUBMITTED → CHARGE_SUCCEEDED / CHARGE_FAILED and PLATFORM_CONFIRMATION_SUBMITTED →
 * PLATFORM_CONFIRMATION_COMPLETE / PLATFORM_CONFIRMATION_FAILED triads.
 *
 * Root-scoped (`providedIn: 'root'`) because the payment lifecycle is driven by `PaymentsState`,
 * a root-singleton NGXS state that can't host per-container providers. The make-payment batch is
 * serialised through the state so a shared operation-timer map keyed by `<stage>:<chargeIndex>`
 * is safe.
 */
@Injectable({ providedIn: 'root' })
export class PaymentTrackService {
  private readonly tracker = inject(TrackWorkflowService) as TrackWorkflowService<PaymentWorkflowEvent, PaymentWorkflowEventMetaDataMap>;
  private readonly operationStarts = new Map<string, number>();

  trackWorkflow<E extends PaymentWorkflowEvent>(step: E, metadata?: PaymentWorkflowEventMetaDataMap[E]): void {
    this.tracker.trackWorkflow(step, metadata);
  }

  startOperation(key: string): void {
    this.operationStarts.set(key, performance.now());
  }

  endOperation(key: string): number {
    const start = this.operationStarts.get(key);
    if (start === undefined) {
      return 0;
    }
    this.operationStarts.delete(key);
    return performance.now() - start;
  }
}

export function providePaymentWorkflowTracking(): EnvironmentProviders {
  return provideWorkflowTracking<PaymentWorkflowEvent, PaymentWorkflowEventMetaDataMap>({
    stepToWorkflow: buildStepToWorkflowMap({ [PaymentsWorkflowType.PAYMENT]: PaymentWorkflowEvent }),
    stepToEventType: PaymentEventEventTypeMap,
  });
}
